import { useEffect, useState } from "react";
import { useAuth } from "../lib/auth";

// Live environment — what THIS pod is actually bound to, read from /api/admin/readiness.
// Read-only by construction: no buttons, no actions, one GET on mount. It exists because the two
// facts below can only be answered by production, and on 12 Oct the deployer's log tools were not
// attached, so the boot lines could not be read at all. A bare URL cannot work either: that endpoint
// takes an Authorization header and the console keeps the access token in memory only — so the
// reading has to happen from inside the console, where the token already is.
// The chain shown is THIS browser's request, which is the useful reading: a real external client.
const yesNo = (b) => (b ? "yes" : "no");

export function LiveEnvironment() {
  const { authedFetch } = useAuth();
  const [v, setV] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    authedFetch("/admin/readiness").then(setV).catch((e) => setErr(e.detail || String(e)));
  }, [authedFetch]);

  const ip = v?.client_ip, ds = v?.document_store;
  const rows = v && [
    ["env", v.env],
    ["mongo", `${v.mongo?.database || "—"} @ ${v.mongo?.resolved_host || "—"}`],
    ["host as expected", v.mongo?.expected_host ? yesNo(v.mongo.resolved_host === v.mongo.expected_host) : "no expectation set"],
    ["migrations applied", `${v.migrations_applied?.length ?? "—"}`],
    ["document store", ds?.backend || "—"],
    ["absent row means", ds?.absent_row_means || "—"],
    ["objstore namespace", ds?.objstore_namespace || "—"],
    ...(ds?.bucket ? [["bucket", `${ds.bucket}${ds.bucket_matches_expected ? "" : " — NOT the expected bucket"}`]] : []),
    ["trusted proxy hops", `${ip?.trusted_hops ?? "—"}`],
    ["your chain", ip?.chain || "none sent"],
    ["hops in chain", `${ip?.chain_len ?? "—"}`],
    ["resolved address", ip?.resolved || "none — falls into the global no-IP ceiling"],
    ["resolved from the trusted end", yesNo(ip?.resolves_from_trusted_end)],
    ["hops you supplied (ignored)", `${ip?.caller_supplied_hops ?? "—"}`],
  ];

  return (
    <div className="gk-panel" data-testid="cos-live-env-panel">
      <h3>Live environment <span className="gk-tag">read-only — this pod, right now</span></h3>
      <p className="gk-muted">Nothing here can be changed from this card. “Trusted proxy hops” is how
        many hops the platform appends to X-Forwarded-For; the caller’s address is read from that end
        of the chain, so hops a caller sends itself are ignored and cannot buy a fresh rate-limit
        bucket. If this host appends a different number, “hops in chain” shows it.</p>
      {err && <div className="gk-msg" data-testid="cos-live-env-err">{err}</div>}
      {!v && !err && <div className="loading" data-testid="cos-live-env-loading">Loading…</div>}
      {v && (
        <table className="gk-table" data-testid="cos-live-env">
          <tbody>
            {rows.map(([k, val]) => (
              <tr key={k} data-testid={`cos-live-env-row-${k.replace(/[^a-z]+/gi, "-")}`}>
                <td className="gk-muted">{k}</td>
                <td className="mono">{val}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
